/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
import { useEffect, useMemo, useState } from "react";
import LoadingSkeletonImageDynamic from "../../../components/LoadingSkeletonImageDynamic";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks";
import { addOrRemoveUserId } from "../../../store/Slices/CreateGroupSlice";
import { useLocation } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import { useTheme } from "../../../context/ThemeProvider";
import { useGroupSettings } from "../../../store/api/useGroupSettings";
import toast from "react-hot-toast";
import TextTranslate from "../../../utils/TextTranslate";
import { updateViewState } from "../../../store/Slices/ViewManagerSlice";
import { useContactList } from "../../../store/api/useContactList";
import { ContactType, FetchUserRes } from "../../../types/ResType";
import { useNavigateToChat } from "../../../hooks/useNavigateToChat";
import { FaCircleCheck } from "react-icons/fa6";
import axiosInstance from "../../../store/api/axiosInstance";

type SelectablePerson = {
  user_id: number;
  name: string;
  user_name: string;
  profile_pic: string;
  updatedAt?: Date | string;
  source: "contact" | "chat";
};

export default function ContactList({ searchUser }: { searchUser: string }) {
  const { data: contactList, isLoading } = useContactList();
  const CreateGroup = useAppSelector((state) => state.CreateGroup);
  const userData = useAppSelector((state) => state.userData);
  const { data: groupSettingsData } = useGroupSettings();
  const OnlineUserList = useAppSelector((state) => state.OnlineUserList);
  const ChatListArray = useAppSelector((state) => state.chatList);
  const archiveList = useAppSelector((state) => state.archiveList);
  const dispatch = useAppDispatch();
  const currentConversationData = useAppSelector(
    (state) => state.CurrentConversation,
  );
  const { theme } = useTheme();
  const location = useLocation();
  const navigateToChat = useNavigateToChat();
  const [remoteUsers, setRemoteUsers] = useState<SelectablePerson[]>([]);
  const [searchingUsers, setSearchingUsers] = useState(false);

  const isContactPicker = location.pathname == "/contact-list";
  const isGroupPicker =
    location.pathname == "/add-member-to-group" ||
    location.pathname == "/create-group";

  function changeCurrentConversation(member: ContactType) {
    if (member.user_id == userData.user_id) {
      return;
    }
    const privateChats = [
      ...(ChatListArray.Chats ?? []),
      ...(archiveList.Chats ?? []),
    ];
    const existing = privateChats.find(
      (chatUser) =>
        chatUser.PeerUserData?.user_id == member.user_id &&
        chatUser.Records?.[0]?.chat_type == "private",
    );
    const chatData = existing?.Records?.[0];
    const isArchived = (archiveList.Chats ?? []).some(
      (chatUser) =>
        chatUser.PeerUserData?.user_id == member.user_id &&
        chatUser.Records?.[0]?.chat_type == "private",
    );

    if (
      currentConversationData.chat_id == chatData?.chat_id &&
      currentConversationData.chat_id != 0
    ) {
      return;
    }

    navigateToChat({
      chat_id: chatData?.chat_id ?? -1,
      chat_type: "private",
      full_name: member.name!,
      group_name: "",
      group_icon: "",
      profile_pic: member.profile_pic!,
      user_id: member.user_id!,
      user_name: member.user_name!,
      unseen_count: 0,
      updatedAt: member.updatedAt,
      is_archived: isArchived,
      is_message_list_empty: chatData?.Messages?.length! <= 0 ? true : false,
      is_group_blocked: chatData?.blocked_by?.includes(
        userData.user_id.toString(),
      ),
      other_user_blocked_you:
        chatData?.blocked_by?.length &&
        chatData?.blocked_by.length >= 1 &&
        !chatData?.blocked_by.includes(userData.user_id.toString())
          ? true
          : false,
    });
  }

  useEffect(() => {
    if (!isContactPicker) {
      setRemoteUsers([]);
      return;
    }
    const q = searchUser.trim().replace(/^@+/, "");
    if (q.length < 2) {
      setRemoteUsers([]);
      setSearchingUsers(false);
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(() => {
      setSearchingUsers(true);
      void axiosInstance
        .post<FetchUserRes>("users/find-user", {
          user_name: q,
          page: 1,
          pageSize: 20,
        })
        .then((res) => {
          if (cancelled) return;
          const records = res.data?.data?.Records ?? [];
          setRemoteUsers(
            records
              .filter((u) => u.user_id && u.user_id !== userData.user_id)
              .map((u) => ({
                user_id: u.user_id,
                name:
                  u.full_name?.trim() ||
                  u.user_name?.trim() ||
                  `#${u.user_id}`,
                user_name: u.user_name || "",
                profile_pic: u.profile_pic || "",
                updatedAt: u.updatedAt,
                source: "chat" as const,
              })),
          );
        })
        .catch(() => {
          if (!cancelled) setRemoteUsers([]);
        })
        .finally(() => {
          if (!cancelled) setSearchingUsers(false);
        });
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [isContactPicker, searchUser, userData.user_id]);

  const { contacts, recentChats, usernameHits } = useMemo(() => {
    const contactMap = new Map<number, SelectablePerson>();
    const chatMap = new Map<number, SelectablePerson>();

    (contactList?.data ?? []).forEach((contact) => {
      if (!contact.user_id || contact.user_id == userData.user_id) return;
      contactMap.set(contact.user_id, {
        user_id: contact.user_id,
        name: contact.name || contact.user_name,
        user_name: contact.user_name,
        profile_pic: contact.profile_pic,
        updatedAt: contact.updatedAt,
        source: "contact",
      });
    });

    if (isGroupPicker) {
      const chats = [
        ...(ChatListArray.Chats ?? []),
        ...(archiveList.Chats ?? []),
      ];
      chats.forEach((chat) => {
        if (chat.Records?.[0]?.chat_type !== "private") return;
        const peer = chat.PeerUserData;
        if (!peer?.user_id || peer.user_id == userData.user_id) return;
        if (contactMap.has(peer.user_id)) return;
        if (chatMap.has(peer.user_id)) return;
        const savedName = contactList?.data?.find(
          (c) => c.user_id === peer.user_id,
        )?.name;
        chatMap.set(peer.user_id, {
          user_id: peer.user_id,
          name:
            savedName?.trim() ||
            peer.full_name?.trim() ||
            peer.user_name?.trim() ||
            `#${peer.user_id}`,
          user_name: peer.user_name || "",
          profile_pic: peer.profile_pic || "",
          updatedAt: peer.updatedAt,
          source: "chat",
        });
      });
    }

    const q = searchUser.trim().toLowerCase();
    const match = (person: SelectablePerson) => {
      if (!q) return true;
      return (
        person.name.toLowerCase().includes(q) ||
        person.user_name.toLowerCase().includes(q)
      );
    };

    const matchedContacts = Array.from(contactMap.values())
      .filter(match)
      .sort((a, b) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
      );
    const knownIds = new Set(matchedContacts.map((p) => p.user_id));

    return {
      contacts: matchedContacts,
      recentChats: Array.from(chatMap.values())
        .filter(match)
        .sort((a, b) =>
          a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
        ),
      usernameHits: remoteUsers.filter((p) => !knownIds.has(p.user_id)),
    };
  }, [
    contactList?.data,
    ChatListArray.Chats,
    archiveList.Chats,
    userData.user_id,
    searchUser,
    isGroupPicker,
    remoteUsers,
  ]);

  const people = isGroupPicker
    ? [...recentChats, ...contacts]
    : [...contacts, ...usernameHits];

  function togglePerson(e: SelectablePerson) {
    if (isContactPicker) {
      changeCurrentConversation({
        name: e.name,
        number: "",
        user_id: e.user_id,
        user_name: e.user_name,
        updatedAt: (e.updatedAt as Date) || new Date(),
        profile_pic: e.profile_pic,
      });
      dispatch(
        updateViewState({
          show_chats_sidebar: false,
        }),
      );
      return;
    }

    const alreadyMember = CreateGroup.existing_member_user_id?.includes(
      e.user_id,
    );
    if (alreadyMember) return;

    if (
      CreateGroup.user_id.length >=
        Number(groupSettingsData?.settings[0].max_members ?? 10) &&
      !CreateGroup.user_id.includes(e.user_id)
    ) {
      toast.error(
        `Max group memeber size is ${groupSettingsData?.settings[0].max_members ?? 10}`,
        { position: "bottom-left" },
      );
      return;
    }
    dispatch(
      addOrRemoveUserId({
        user_id: e.user_id,
      }),
    );
  }

  function renderPersonRow(e: SelectablePerson) {
    const selected = CreateGroup.user_id.includes(e.user_id);
    const alreadyMember = CreateGroup.existing_member_user_id?.includes(
      e.user_id,
    );
    return (
      <button
        type="button"
        key={`${e.source}-${e.user_id}`}
        disabled={alreadyMember}
        onClick={() => togglePerson(e)}
        className={`flex w-full items-center gap-3 px-3 py-3 text-left transition active:bg-black/[0.03] dark:active:bg-white/[0.04] ${
          selected || alreadyMember ? "bg-selectedChat" : ""
        } ${alreadyMember ? "opacity-60" : ""}`}
      >
        <div className="relative h-12 w-12 shrink-0">
          <LoadingSkeletonImageDynamic
            radius=""
            className="h-12 w-12 rounded-full object-cover"
            image_height="100%"
            image_url={e.profile_pic}
            image_width=""
          />
          {OnlineUserList.onlineUsers.find(
            (user) => user.user_id == e.user_id,
          ) && (
            <img
              className="absolute bottom-0 right-0 z-30 h-3.5 w-3.5"
              src="/Home/Online_Green_dot.png"
              alt=""
            />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[16px] font-semibold capitalize text-darkText">
            {e.name}
          </div>
          {e.user_name ? (
            <div className="mt-0.5 truncate text-[13px] text-lightText">
              @{e.user_name.replace(/^@+/, "")}
            </div>
          ) : null}
        </div>
        {(selected || alreadyMember) && (
          <FaCircleCheck className="h-5 w-5 shrink-0 text-themeColor" />
        )}
      </button>
    );
  }

  if ((isLoading || searchingUsers) && people.length === 0) {
    return (
      <div className="grid h-48 place-content-center">
        <ClipLoader size={23} color={theme == "dark" ? "white" : "black"} />
      </div>
    );
  }

  if (people.length === 0) {
    if (isContactPicker && searchUser.trim().replace(/^@+/, "").length >= 2) {
      return (
        <div className="grid place-content-center gap-3 px-5 py-16 text-center text-[14px] text-lightText">
          <TextTranslate text="No users found" />
        </div>
      );
    }
    if (isContactPicker) {
      return (
        <div className="grid place-content-center gap-4 px-5 py-16 text-center text-[14px] text-lightText">
          <img
            className="mx-auto h-16 w-16"
            src="/LightIcons/contact_not_found_light.png"
            alt=""
          />
          <div>
            Install the Whoxa mobile app first to sync your account and display
            your contacts here.
          </div>
        </div>
      );
    }

    return (
      <div className="grid place-content-center gap-3 px-5 py-16 text-center text-[14px] text-lightText">
        <TextTranslate text="No contacts found" />
      </div>
    );
  }

  if (!isGroupPicker) {
    return (
      <div className="[&>*+*]:border-t [&>*+*]:border-black/[0.06] dark:[&>*+*]:border-white/[0.08]">
        {people.map(renderPersonRow)}
        {searchingUsers && (
          <div className="grid place-content-center py-4">
            <ClipLoader size={18} color={theme == "dark" ? "white" : "black"} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {recentChats.length > 0 && (
        <div>
          <div className="mb-1.5 px-1 text-[13px] font-medium uppercase tracking-wide text-lightText">
            <TextTranslate text="Recent chats" />
          </div>
          <div className="overflow-hidden rounded-2xl bg-primary shadow-sm ring-1 ring-black/[0.04] dark:ring-white/[0.06]">
            <div className="[&>*+*]:border-t [&>*+*]:border-black/[0.06] dark:[&>*+*]:border-white/[0.08]">
              {recentChats.map(renderPersonRow)}
            </div>
          </div>
        </div>
      )}

      {contacts.length > 0 && (
        <div>
          <div className="mb-1.5 px-1 text-[13px] font-medium uppercase tracking-wide text-lightText">
            <TextTranslate text="Contacts" />
          </div>
          <div className="overflow-hidden rounded-2xl bg-primary shadow-sm ring-1 ring-black/[0.04] dark:ring-white/[0.06]">
            <div className="[&>*+*]:border-t [&>*+*]:border-black/[0.06] dark:[&>*+*]:border-white/[0.08]">
              {contacts.map(renderPersonRow)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
